import React, { useState, useEffect } from "react";
import { useOrganization } from "../../../context/OrganizationContext";
import { BillingInfo, PlanType, BillingCycle } from "../../../types/billing";
import { billingService } from "../../../services/billingService";
import { BillingOverview } from "./BillingOverview";
import { SmartAlert } from "./SmartAlert";
import { SubscriptionManagerV2 } from "./SubscriptionManagerV2";
import { BillingPortalCard } from "./BillingPortalCard";
import { UsageIndicators } from "./UsageIndicators";

export const BillingDashboardV2: React.FC = () => {
  const { selectedOrg } = useOrganization();
  const [billingData, setBillingData] = useState<BillingInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [primaryAlert, setPrimaryAlert] = useState<{
    type: "info" | "warning" | "error" | "success";
    message: string;
    actions?: Array<{ label: string; onClick: () => void }>;
  } | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (selectedOrg) {
      fetchBillingLimits();
    }
  }, [selectedOrg]);

  const fetchBillingLimits = async () => {
    if (!selectedOrg) return;

    try {
      setLoading(true);
      const data = await billingService.getBillingLimits(selectedOrg.id);
      setBillingData(data);
      analyzeBillingState(data);
    } catch (error) {
      console.error("Failed to fetch billing data:", error);
      setPrimaryAlert({
        type: "error",
        message: "Failed to load billing information. Please try again.",
        actions: [{ label: "Retry", onClick: fetchBillingLimits }],
      });
    } finally {
      setLoading(false);
    }
  };

  const analyzeBillingState = (data: BillingInfo) => {
    // Smart alert logic - only show the most important message

    // Critical: Trial expiring soon
    if (data.is_trial_active && data.trial_end_date) {
      const daysLeft = Math.ceil(
        (new Date(data.trial_end_date).getTime() - Date.now()) /
          (1000 * 60 * 60 * 24),
      );
      if (daysLeft <= 3) {
        setPrimaryAlert({
          type: "warning",
          message: `Your trial expires in ${daysLeft} day${daysLeft === 1 ? "" : "s"}. Upgrade now to continue using all features.`,
          actions: [
            {
              label: "Upgrade Now",
              onClick: () => setIsExpanded(true),
            },
          ],
        });
        return;
      }
    }

    // Important: Scheduled changes
    if (data.scheduled_changes) {
      setPrimaryAlert({
        type: "info",
        message: `Plan change scheduled for ${new Date(data.scheduled_changes.effective_date).toLocaleDateString()}`,
        actions: [
          {
            label: "View Details",
            onClick: () => setIsExpanded(true),
          },
        ],
      });
      return;
    }

    // Usage warnings
    if (data.max_editors && data.current_editors >= data.max_editors * 0.9) {
      setPrimaryAlert({
        type: "warning",
        message: `You're using ${data.current_editors} of ${data.max_editors} editor seats.`,
        actions: [
          {
            label: "Add More Seats",
            onClick: () => setIsExpanded(true),
          },
        ],
      });
      return;
    }

    // Clear alert if nothing important
    setPrimaryAlert(null);
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-32 bg-gray-200 rounded-lg"></div>
        <div className="h-24 bg-gray-200 rounded-lg"></div>
      </div>
    );
  }

  if (!billingData || !selectedOrg) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No billing information available</p>
      </div>
    );
  }

  const isManualBilling = billingData.billing_type === "manual";
  const hasPaidPlan = billingData.plan_type !== "free";
  const hasActiveSubscription =
    !!billingData.subscription_status &&
    !["canceled", "unpaid"].includes(billingData.subscription_status);

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      {/* Primary: Unified Billing Overview - Always Visible */}
      <BillingOverview
        billingData={billingData}
        onPrimaryAction={() => setIsExpanded(!isExpanded)}
        isExpanded={isExpanded}
      />

      {/* Smart Alert - Only shows most important message */}
      {primaryAlert && (
        <SmartAlert
          type={primaryAlert.type}
          message={primaryAlert.message}
          actions={primaryAlert.actions}
          onDismiss={() => setPrimaryAlert(null)}
        />
      )}

      {/* Usage Indicators - Only if approaching limits */}
      <UsageIndicators
        billingData={billingData}
        onUpgradeClick={() => setIsExpanded(true)}
      />

      {/* Expandable Management Section */}
      {isExpanded && (
        <div className="space-y-6 animate-fadeIn">
          {/* Subscription Management - Contextual based on state */}
          {!isManualBilling && (
            <SubscriptionManagerV2
              billingData={billingData}
              onUpdate={fetchBillingLimits}
            />
          )}

          {/* Billing Portal Access - Simplified */}
          {billingData.billing_type === "stripe" && hasActiveSubscription && (
            <BillingPortalCard
              organizationId={selectedOrg.id}
              currentPeriodEnd={billingData.current_period_end}
            />
          )}
        </div>
      )}
    </div>
  );
};
